import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ProIcon } from './ProIcon';
import { trackEvent, trackGenerateLead } from '../lib/analytics';
import { isValidNewsletterEmail, submitNewsletterLead } from '../lib/newsletter';

/**
 * NewsletterSignup — reusable email-capture component for soft conversions.
 *
 * Purpose:
 *   Capture browsing guests who are not ready to enquire or book yet.
 *   Each placement adapts copy, styling, and analytics context while
 *   sharing a single markup tree.
 *
 * Variants:
 *   - feature  — hero-sized card on Home (white bg, large radius, shadow)
 *   - inline   — lighter card after Listing results or no-results state
 *   - footer   — dark-theme compact card inside the site footer
 *
 * Features:
 *   - GDPR-compliant consent checkbox with Privacy Policy link
 *   - Honeypot field for basic bot filtering
 *   - Full GA4 analytics: view → submit → success / error
 *   - UTM context captured in payload for Campaign Monitor attribution
 *   - Unique IDs via useId() — safe for multiple instances per page
 *   - Duplicate-subscriber detection with friendly messaging
 *   - Responsive: benefit chips hidden on mobile, 2-col grid at lg/xl
 *
 * Integration (future):
 *   Swap `submitNewsletterLead` in /src/lib/newsletter.ts from the
 *   localStorage demo to a Campaign Monitor API call. The component
 *   itself needs no changes.
 *
 * Props:
 *   @param variant        — 'feature' | 'inline' | 'footer'
 *   @param source         — analytics source identifier (e.g. 'home_bottom')
 *   @param title          — main heading text
 *   @param description    — supporting paragraph
 *   @param eyebrow        — small label above the title
 *   @param buttonLabel    — CTA button text
 *   @param tags           — optional tags sent with the lead payload
 *   @param favoritesCount — optional saved-rentals count for analytics
 *   @param className      — extra classes on the outer section
 */
type NewsletterVariant = 'feature' | 'footer' | 'inline';

interface NewsletterSignupProps {
  buttonLabel?: string;
  className?: string;
  description: string;
  eyebrow?: string;
  favoritesCount?: number;
  source: string;
  tags?: string[];
  title: string;
  variant?: NewsletterVariant;
}

const SESSION_SIGNED_UP_KEY = 'sunmarbella_newsletter_signed_up';

const BENEFIT_ITEMS = [
  { icon: 'hourglass', family: 'utility' as const, label: 'Last-minute openings' },
  { icon: 'percent', family: 'light' as const, label: 'Price-drop alerts' },
  { icon: 'bullseye-arrow', family: 'light' as const, label: 'Direct-booking offers' },
];

// Small style map so each placement stays visually related without duplicating markup.
const VARIANT_STYLES: Record<
  NewsletterVariant,
  {
    container: string;
    description: string;
    eyebrow: string;
    formShell: string;
    input: string;
    benefitChip: string;
    consent: string;
    success: string;
    button: string;
  }
> = {
  feature: {
    container:
      'relative overflow-hidden rounded-[2rem] border border-slate-100 bg-white px-6 py-8 shadow-xl shadow-brand-ocean/10 sm:px-8 sm:py-10 lg:px-12 lg:py-12',
    description: 'text-base text-slate-600 leading-relaxed',
    eyebrow: 'ui-eyebrow-brand',
    formShell: 'rounded-[1.75rem] border border-brand-ocean/10 bg-slate-50/90 p-4 sm:p-5',
    input:
      'ui-input min-h-[52px] rounded-2xl border-slate-200 bg-white px-4 text-base shadow-sm',
    benefitChip:
      'inline-flex items-center gap-2 rounded-full border border-brand-ocean/10 bg-brand-ocean/5 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-brand-ocean',
    consent: 'text-sm text-slate-500 leading-relaxed',
    success:
      'rounded-[1.75rem] border border-emerald-200 bg-emerald-50 px-5 py-5 text-emerald-900',
    button: 'ui-btn-primary min-h-[52px] w-full xl:w-auto xl:min-w-[180px]',
  },
  inline: {
    container:
      'relative overflow-hidden rounded-xl border border-slate-100 bg-white px-3 py-4 shadow-sm sm:rounded-2xl sm:px-5 sm:py-5 md:rounded-[1.75rem] md:px-6 md:py-6',
    description: 'text-xs sm:text-sm text-slate-600 leading-relaxed max-w-prose',
    eyebrow: 'ui-eyebrow-brand',
    formShell: 'rounded-lg border border-slate-200 bg-slate-50 p-2.5 sm:rounded-xl sm:p-3 md:rounded-[1.25rem] md:p-4',
    input:
      'ui-input min-h-[40px] rounded-lg border-slate-200 bg-white px-3 text-sm shadow-sm sm:min-h-[44px] sm:rounded-xl sm:px-4 md:min-h-[46px] md:rounded-2xl md:text-base',
    benefitChip:
      'inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2 py-1 text-2xs font-bold uppercase tracking-[0.1em] text-slate-600',
    consent: 'text-xs sm:text-sm text-slate-500 leading-snug',
    success:
      'rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3 text-emerald-900 sm:rounded-xl sm:px-4 sm:py-4 md:rounded-[1.25rem]',
    button: 'ui-btn-primary min-h-[40px] w-full text-sm sm:min-h-[44px] md:w-auto md:min-w-[140px] md:min-h-[46px] md:text-base',
  },
  footer: {
    container:
      'relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/5 px-5 py-6 backdrop-blur-sm sm:px-7 sm:py-7',
    description: 'text-sm sm:text-base text-white/75 leading-relaxed',
    eyebrow: 'ui-eyebrow text-brand-sun',
    formShell: 'rounded-[1.5rem] border border-white/10 bg-slate-950/30 p-4',
    input:
      'min-h-[50px] w-full rounded-2xl border border-white/10 bg-white px-4 text-base text-slate-900 placeholder:text-slate-400 outline-none transition-all focus:border-brand-sun focus:ring-2 focus:ring-brand-sun/20',
    benefitChip:
      'inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-white/85',
    consent: 'text-sm text-white/65 leading-relaxed',
    success:
      'rounded-[1.5rem] border border-emerald-400/20 bg-emerald-400/10 px-5 py-5 text-emerald-50',
    button: 'ui-btn-primary min-h-[50px] w-full sm:w-auto sm:min-w-[160px]',
  },
};

export const NewsletterSignup: React.FC<NewsletterSignupProps> = ({
  buttonLabel = 'Get Deals',
  className = '',
  description,
  eyebrow = 'Stay in the Loop',
  favoritesCount,
  source,
  tags,
  title,
  variant = 'inline',
}) => {
  const checkboxId = useId();
  const location = useLocation();
  const hasTrackedView = useRef(false);
  const styles = VARIANT_STYLES[variant];
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_SIGNED_UP_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (hasTrackedView.current) return;
    hasTrackedView.current = true;

    trackEvent('newsletter_view', {
      source,
      variant,
      page_path: location.pathname,
      favorites_count: favoritesCount,
    });
  }, [favoritesCount, location.pathname, source, variant]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();

    if (!isValidNewsletterEmail(normalizedEmail)) {
      setError('Enter a valid email address to get deal alerts.');
      return;
    }

    if (!consent) {
      setError('Please confirm you want to receive email updates.');
      return;
    }

    setError('');
    setIsSubmitting(true);

    trackEvent('newsletter_submit', {
      source,
      variant,
      page_path: location.pathname,
      favorites_count: favoritesCount,
    });

    try {
      const result = await submitNewsletterLead({
        consent,
        email: normalizedEmail,
        favoritesCount,
        honeypot,
        pathname: location.pathname,
        source,
        tags,
      });

      const nextMessage =
        result.status === 'already_subscribed'
          ? 'You are already on the list. We will keep sending the best openings and offers.'
          : 'You are in. We will send last-minute openings, price drops, and direct-booking offers.';

      setSuccessMessage(nextMessage);
      setEmail('');
      setConsent(false);
      setHoneypot('');

      trackEvent('newsletter_success', {
        source,
        variant,
        page_path: location.pathname,
        signup_status: result.status,
        storage: result.storage,
      });
      trackGenerateLead(`newsletter_${source}`);

      try {
        sessionStorage.setItem(SESSION_SIGNED_UP_KEY, '1');
      } catch { /* private browsing */ }

      setTimeout(() => setDismissed(true), 4000);
    } catch (submitError) {
      const nextError =
        submitError instanceof Error
          ? submitError.message
          : 'Unable to save your signup right now.';

      setError(nextError);
      trackEvent('newsletter_error', {
        source,
        variant,
        page_path: location.pathname,
        error_message: nextError,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (dismissed) return null;

  return (
    <section className={`${styles.container} ${className}`}>
      <div className="pointer-events-none absolute -right-16 top-0 hidden h-48 w-48 rounded-full bg-brand-sun/10 blur-3xl sm:block" />
      <div className="pointer-events-none absolute -left-16 bottom-0 hidden h-40 w-40 rounded-full bg-brand-ocean/10 blur-3xl sm:block" />

      <div
        className={`relative z-10 grid gap-4 sm:gap-6 ${
          variant === 'feature'
            ? 'lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)] lg:items-center'
            : variant === 'footer'
              ? 'lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.95fr)] lg:items-center'
              : ''
        }`}
      >
        <div className={variant === 'inline' ? 'space-y-2' : 'space-y-4'}>
          <div className={variant === 'inline' ? 'space-y-1.5' : 'space-y-3'}>
            <p className={styles.eyebrow}>{eyebrow}</p>
            <h2
              className={`font-black tracking-tight ${
                variant === 'footer'
                  ? 'text-xl sm:text-2xl'
                  : variant === 'inline'
                    ? 'text-lg sm:text-xl'
                    : 'text-2xl sm:text-3xl'
              } ${
                variant === 'footer' ? 'text-white' : 'text-brand-ocean'
              }`}
            >
              {title}
            </h2>
            <p className={styles.description}>{description}</p>
          </div>

          {variant === 'feature' ? (
            <div className="hidden flex-wrap gap-2.5 sm:flex">
              {BENEFIT_ITEMS.map((item) => (
                <span key={item.label} className={styles.benefitChip}>
                  <ProIcon icon={item.icon} family={item.family} className="h-3.5 w-3.5" />
                  <span>{item.label}</span>
                </span>
              ))}
            </div>
          ) : null}
        </div>

        <div className={styles.formShell}>
          {successMessage ? (
            <div className={styles.success} aria-live="polite">
              <div className={`flex items-start ${variant === 'inline' ? 'gap-2' : 'gap-3'}`}>
                <ProIcon icon="circle-check" family="light" className={`mt-0.5 flex-shrink-0 ${variant === 'inline' ? 'h-4 w-4' : 'h-5 w-5'}`} />
                <div className="space-y-1">
                  <p className={`font-black uppercase tracking-[0.12em] ${variant === 'inline' ? 'text-xs' : 'text-sm'}`}>You are on the list</p>
                  <p className={`leading-relaxed ${variant === 'inline' ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'}`}>{successMessage}</p>
                </div>
              </div>
            </div>
          ) : (
            <form className={variant === 'inline' ? 'space-y-3' : 'space-y-4'} onSubmit={handleSubmit} noValidate>
              <div className={variant === 'inline' ? 'space-y-2' : 'space-y-3'}>
                <label htmlFor={`${checkboxId}-email`} className="sr-only">
                  Email address
                </label>
                <div className={`flex flex-col gap-2 sm:gap-3 ${variant === 'feature' ? 'xl:flex-row' : 'md:flex-row'}`}>
                  <div className="relative min-w-0 flex-1">
                    <ProIcon
                      icon="envelope-open"
                      family="light"
                      className="pointer-events-none absolute left-3 sm:left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      id={`${checkboxId}-email`}
                      type="email"
                      value={email}
                      onChange={(event) => {
                        setEmail(event.target.value);
                        if (error) setError('');
                      }}
                      placeholder="you@example.com"
                      className={`${styles.input} pl-9 sm:pl-11 w-full`}
                      autoComplete="email"
                      inputMode="email"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`${styles.button} ${isSubmitting ? 'cursor-not-allowed opacity-70' : ''}`}
                  >
                    <span>{isSubmitting ? 'Joining...' : buttonLabel}</span>
                  </button>
                </div>
              </div>

              <div className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
                <label htmlFor={`${checkboxId}-website`}>Website</label>
                <input
                  id={`${checkboxId}-website`}
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(event) => setHoneypot(event.target.value)}
                />
              </div>

              <div className="flex items-start gap-2 sm:gap-3">
                <input
                  id={checkboxId}
                  type="checkbox"
                  checked={consent}
                  onChange={(event) => {
                    setConsent(event.target.checked);
                    if (error) setError('');
                  }}
                  className={`mt-0.5 sm:mt-1 h-4 w-4 rounded text-brand-sun focus:ring-brand-sun ${
                    variant === 'footer' ? 'border-white/40 bg-white/10' : 'border-slate-300'
                  }`}
                />
                <div className={styles.consent}>
                  <label htmlFor={checkboxId} className="cursor-pointer">
                    I want last-minute openings, direct-booking offers, and occasional email updates.
                  </label>
                  <span className="block">
                    By signing up, you agree to our{' '}
                    <Link
                      to="/privacy-policy"
                      className={`font-semibold underline decoration-current/40 underline-offset-4 hover:decoration-current ${
                        variant === 'footer' ? 'text-white' : 'text-brand-ocean'
                      }`}
                    >
                      Privacy Policy
                    </Link>
                    .
                  </span>
                </div>
              </div>

              {error ? (
                <p
                  className={`border px-3 py-2.5 text-xs font-medium sm:px-4 sm:py-3 sm:text-sm ${
                    variant === 'inline'
                      ? 'rounded-lg sm:rounded-xl'
                      : variant === 'footer'
                        ? 'rounded-xl sm:rounded-2xl'
                        : 'rounded-xl sm:rounded-2xl'
                  } ${
                    variant === 'footer'
                      ? 'border-red-400/20 bg-red-500/10 text-red-100'
                      : 'border-red-200 bg-red-50 text-red-700'
                  }`}
                  aria-live="polite"
                >
                  {error}
                </p>
              ) : null}
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
